import React, { useState, useEffect } from 'react';
import { Outlet, Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAvatar } from '../context/AvatarContext';
import { auth, API_BASE_URL, getAuthHeader } from '../utils/api';
import { wsClient } from '../utils/websocket';
import AdminButton from './AdminButton';
import NotificationBell from './NotificationBell';
const Layout = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const { avatarUrl, handleImageError } = useAvatar();
  const { user, loading, logout, updateUser, refreshTokens } = useAuth();
  const navigate = useNavigate();
  const profileMenuRef = React.useRef();
  const mobileMenuRef = React.useRef();

  // Track if we've attempted WebSocket connection
  const wsInitialized = React.useRef(false);

  // Initialize WebSocket connection
  useEffect(() => {
    if (!user || loading || wsInitialized.current) {
      return;
    }

    // Initialize WebSocket connection
    const initializeWebSocket = async () => {
      try {
        console.debug('Fetching WebSocket token');
        const response = await fetch(`${API_BASE_URL}/users/auth/me`, {
          headers: await getAuthHeader('GET'),
          credentials: 'include'
        });
        
        const wsToken = response.headers.get('x-ws-token');
        console.debug('WebSocket token received:', wsToken ? 'yes' : 'no');
        
        if (!wsToken) {
          console.error('No WebSocket token received in response headers');
          return;
        }
        
        if (!wsClient.isConnected()) {
          console.debug('Connecting WebSocket with token');
          wsClient.connect(wsToken);
          wsInitialized.current = true;
        }
      } catch (error) {
        console.error('Failed to initialize WebSocket:', error);
      }
    };

    console.debug('User authenticated, initializing WebSocket');
    initializeWebSocket();

    // Only close when user becomes null (logout)
    return () => {
      if (!user && wsClient.isConnected()) {
        console.debug('Closing WebSocket connection on logout');
        wsClient.close();
      }
    };
  }, [user, loading]); // Only depend on user and loading

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) && !event.target.closest('button[aria-controls="mobile-menu"]')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    await logout(); // AuthContext will handle the redirect
  };

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      <nav className="bg-white shadow">
        <div className="w-full px-4">
          <div className="flex justify-between h-14 md:h-16">
            <div className="flex items-center">
              {/* Mobile menu button */}
              {/* Mobile menu button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                type="button"
                className="lg:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                aria-controls="mobile-menu"
                aria-expanded={isMobileMenuOpen}
              >
                <span className="sr-only">Open main menu</span>
                <svg className={`${isMobileMenuOpen ? 'hidden' : 'block'} h-6 w-6`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                <svg className={`${isMobileMenuOpen ? 'block' : 'hidden'} h-6 w-6`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div className="flex-shrink-0 flex items-center">
                <Link to="/" className="text-lg sm:text-xl font-bold text-indigo-600">
                  NumisNavigator
                </Link>
              </div>
              <div className="hidden lg:flex lg:items-center lg:ml-4 overflow-x-auto max-w-[calc(100vw-19rem)] relative [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 hover:[&::-webkit-scrollbar-thumb]:bg-gray-400">
                <div className="flex space-x-1 sm:space-x-2 md:space-x-3 lg:space-x-6">
                  <NavLink
                    to="/dashboard"
                    className={({ isActive }) =>
                      `${isActive ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'} inline-flex items-center px-0.5 sm:px-1 pt-1 border-b-2 text-xs sm:text-sm font-medium h-14 md:h-16 shrink-0`
                    }
                  >
                    Dashboard
                  </NavLink>
                  <NavLink
                    to="/inventory"
                    className={({ isActive }) =>
                      `${isActive ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'} inline-flex items-center px-0.5 sm:px-1 pt-1 border-b-2 text-xs sm:text-sm font-medium h-14 md:h-16 shrink-0`
                    }
                  >
                    Inventory
                  </NavLink>
                  <NavLink
                    to="/sets"
                    className={({ isActive }) =>
                      `${isActive ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'} inline-flex items-center px-0.5 sm:px-1 pt-1 border-b-2 text-xs sm:text-sm font-medium h-14 md:h-16 shrink-0`
                    }
                  >
                    Sets
                  </NavLink>
                  <NavLink
                    to="/transactions"
                    className={({ isActive }) =>
                      `${isActive ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'} inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-14 md:h-16 shrink-0`
                    }
                  >
                    Transactions
                  </NavLink>
                  <NavLink
                    to="/contacts"
                    className={({ isActive }) =>
                      `${isActive ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'} inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-14 md:h-16 shrink-0`
                    }
                  >
                    Contacts
                  </NavLink>
                  <NavLink
                    to="/submissions"
                    className={({ isActive }) =>
                      `${isActive ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'} inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-14 md:h-16 shrink-0`
                    }
                  >
                    Submissions
                  </NavLink>
                  <NavLink
                    to="/documents"
                    className={({ isActive }) =>
                      `${isActive ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'} inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-14 md:h-16 shrink-0`
                    }
                  >
                    Documents
                  </NavLink>
                  <NavLink
                    to="/auctions"
                    className={({ isActive }) =>
                      `${isActive ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'} inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-14 md:h-16 shrink-0`
                    }
                  >
                    Auction Planner
                  </NavLink>
                  <div className="border-transparent text-gray-400 inline-flex items-center px-0.5 sm:px-1 pt-1 border-b-2 text-sm font-medium h-14 md:h-16 shrink-0">
                    Market Research
                    <span className="ml-1 px-1.5 py-0.5 text-[10px] font-medium bg-indigo-100 text-indigo-800 rounded-full">soon</span>
                  </div>
                  <div className="border-transparent text-gray-400 inline-flex items-center px-0.5 sm:px-1 pt-1 border-b-2 text-sm font-medium h-14 md:h-16 shrink-0">
                    Reports
                    <span className="ml-1 px-1.5 py-0.5 text-[10px] font-medium bg-indigo-100 text-indigo-800 rounded-full">soon</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 md:ml-6">
              <AdminButton />
              <NotificationBell />

              <div className="relative" ref={profileMenuRef}>
                <div>
                  <button
                    type="button"
                    className="bg-white rounded-full flex text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    id="user-menu-button"
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                  >
                    <span className="sr-only">Open user menu</span>
                    {user?.avatar_url ? (
                      <img
                        className="h-8 w-8 rounded-full object-cover"
                        src={avatarUrl}
                        alt="Profile"
                        onError={handleImageError}
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center">
                        <span className="text-sm font-medium text-indigo-600">
                          {user?.first_name && user?.last_name 
                            ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
                            : user?.email?.[0]?.toUpperCase() || '?'}
                        </span>
                      </div>
                    )}
                  </button>
                </div>
                {isProfileMenuOpen && (
                  <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsProfileMenuOpen(false)}
                    >
                      Profile Settings
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile menu, show/hide based on menu state. */}
        {isMobileMenuOpen && (
          <div className="lg:hidden" id="mobile-menu" ref={mobileMenuRef}>
            <div className="pt-2 pb-4 space-y-1">
              <NavLink
                to="/dashboard"
                className={({ isActive }) =>
                  `${isActive ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'} block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-all duration-150`
                }
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Dashboard
              </NavLink>
              <NavLink
                to="/inventory"
                className={({ isActive }) =>
                  `${isActive ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'} block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-all duration-150`
                }
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Inventory
              </NavLink>
              <NavLink
                to="/sets"
                className={({ isActive }) =>
                  `${isActive ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'} block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-all duration-150`
                }
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Sets
              </NavLink>
              <NavLink
                to="/transactions"
                className={({ isActive }) =>
                  `${isActive ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'} block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-all duration-150`
                }
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Transactions
              </NavLink>
              <NavLink
                to="/contacts"
                className={({ isActive }) =>
                  `${isActive ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'} block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-all duration-150`
                }
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Contacts
              </NavLink>
              <NavLink
                to="/submissions"
                className={({ isActive }) =>
                  `${isActive ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'} block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-all duration-150`
                }
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Submissions
              </NavLink>
              <NavLink
                to="/documents"
                className={({ isActive }) =>
                  `${isActive ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'} block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-all duration-150`
                }
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Documents
              </NavLink>
              <NavLink
                to="/auctions"
                className={({ isActive }) =>
                  `${isActive ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'} block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-all duration-150`
                }
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Auction Planner
              </NavLink>
              <div className="border-transparent text-gray-400 block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-all duration-150">
                Market Research
                <span className="ml-1 px-1.5 py-0.5 text-[10px] font-medium bg-indigo-100 text-indigo-800 rounded-full">soon</span>
              </div>
              <div className="border-transparent text-gray-400 block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-all duration-150">
                Reports
                <span className="ml-1 px-1.5 py-0.5 text-[10px] font-medium bg-indigo-100 text-indigo-800 rounded-full">soon</span>
              </div>
            </div>
            
            {/* Mobile menu user section */}
            <div className="pt-4 pb-3 border-t border-gray-200">
              <div className="flex items-center px-4">
                {user?.avatar_url ? (
                  <img
                    className="h-10 w-10 rounded-full object-cover"
                    src={avatarUrl}
                    alt="Profile"
                    onError={handleImageError}
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                    <span className="text-sm font-medium text-indigo-600">
                      {user?.first_name && user?.last_name 
                        ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
                        : user?.email?.[0]?.toUpperCase() || '?'}
                    </span>
                  </div>
                )}
                <div className="ml-3">
                  <div className="text-base font-medium text-gray-800">
                    {user?.first_name && user?.last_name 
                      ? `${user.first_name} ${user.last_name}`
                      : user?.email}
                  </div>
                  <div className="text-sm font-medium text-gray-500">
                    {user?.email}
                  </div>
                </div>
              </div>
              <div className="mt-3 space-y-1">
                <Link
                  to="/profile"
                  className="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors duration-150"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Profile Settings
                </Link>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors duration-150"
                >
                  Sign out
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      <div className="flex-1 relative overflow-hidden">
        <main className="h-full">
          <div className="w-full h-full overflow-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4" style={{ isolation: 'isolate' }}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
